// Finnhub free tier: real-time US quotes, symbol search, company profile, basic metrics. No candles on the free plan.
import { t } from '@/i18n';
import type { Market, Quote, SymbolListing } from '@/types';
import { MarketError, marketFetch } from './errors';

const BASE = 'https://finnhub.io/api/v1';

// The key goes in the `token` query param, not the `X-Finnhub-Token` header: Finnhub's CORS preflight
// response carries no Access-Control-Allow-Headers, so any custom header makes the browser reject the
// request before it is sent. A plain GET with only query params needs no preflight.
function get(path: string, params: Record<string, string>, apiKey: string, signal?: AbortSignal) {
  const url = new URL(`${BASE}${path}`);
  for (const [k, v] of Object.entries(params)) url.searchParams.set(k, v);
  url.searchParams.set('token', apiKey);
  return marketFetch(url.toString(), { signal }, 'Finnhub');
}

interface FinnhubQuote { c: number; d: number | null; dp: number | null; o?: number; h?: number; l?: number; pc: number; t: number }

const CA_SUFFIX: Record<string, string> = { XTSE: '.TO', XTSX: '.V', XCNQ: '.CN', NEOE: '.NE' };

/** Finnhub's ticker for a listing: bare for US, exchange-suffixed for Canada (served on paid plans only). */
export function finnhubSymbol(symbol: string, market?: Market, mic?: string): string {
  const s = symbol.toUpperCase();
  return market === 'CA' ? s + (CA_SUFFIX[mic ?? ''] ?? '.TO') : s;
}

export async function finnhubQuote(symbol: string, apiKey: string, signal?: AbortSignal, listing?: { market?: Market; mic?: string }): Promise<Quote> {
  const q = (await (await get('/quote', { symbol: finnhubSymbol(symbol, listing?.market, listing?.mic) }, apiKey, signal)).json()) as FinnhubQuote;
  if (!q.c || q.c <= 0) throw new MarketError('not_found', t('err.noQuote', { label: 'Finnhub', symbol }));
  const ca = listing?.market === 'CA';
  return {
    key: ca ? `eq:CA:${symbol.toUpperCase()}` : `eq:${symbol.toUpperCase()}`,
    price: q.c,
    currency: ca ? 'CAD' : 'USD',
    asOf: q.t ? q.t * 1000 : Date.now(),
    change: q.d ?? undefined,
    changePct: q.dp ?? undefined,
    previousClose: q.pc || undefined,
    open: q.o || undefined,
    high: q.h || undefined,
    low: q.l || undefined,
    source: 'finnhub',
    freshness: 'realtime',
    stale: false,
  };
}

export interface SymbolHit { symbol: string; name: string; type: string; exchange?: string; market?: Market; mic?: string; currency?: string }

export async function finnhubSearch(query: string, apiKey: string, signal?: AbortSignal): Promise<SymbolHit[]> {
  const json = (await (await get('/search', { q: query, exchange: 'US' }, apiKey, signal)).json()) as { result?: { symbol: string; description: string; type: string; displaySymbol: string }[] };
  // `exchange=US` already restricts results to US listings. Do not filter on '.': US share classes use it
  // (BRK.A, BRK.B, BF.B, LEN.B) and every downstream provider (quote, Massive, Google Finance, TradingView) accepts it.
  // Do not truncate here either: Finnhub's own ordering is not by symbol relevance, so a query like "BRK" can
  // list BRK.A / BRK.B far down. search.ts ranks and cuts the list.
  return (json.result ?? []).map((r) => ({ symbol: r.symbol, name: r.description, type: r.type }));
}

/** Full US listing (~30k rows, a few MB) that feeds the local symbol index; free tier, one call. */
export async function finnhubSymbols(apiKey: string, signal?: AbortSignal): Promise<SymbolListing[]> {
  const rows = (await (await get('/stock/symbol', { exchange: 'US' }, apiKey, signal)).json()) as { symbol?: string; description?: string; type?: string; mic?: string }[];
  return (Array.isArray(rows) ? rows : [])
    .filter((r): r is { symbol: string; description: string; type?: string; mic?: string } => !!r.symbol && !!r.description)
    .map((r) => ({ symbol: r.symbol, name: r.description, type: r.type ?? '', mic: r.mic ?? '' }));
}

export interface CompanyProfile { name: string; exchange?: string; industry?: string; marketCap?: number; logo?: string; weburl?: string }

export async function finnhubProfile(symbol: string, apiKey: string, signal?: AbortSignal): Promise<CompanyProfile | null> {
  const p = (await (await get('/stock/profile2', { symbol }, apiKey, signal)).json()) as { name?: string; exchange?: string; finnhubIndustry?: string; marketCapitalization?: number; logo?: string; weburl?: string };
  if (!p.name) return null;
  return { name: p.name, exchange: p.exchange, industry: p.finnhubIndustry, marketCap: p.marketCapitalization, logo: p.logo, weburl: p.weburl };
}

export interface BasicMetrics { pe?: number; high52?: number; low52?: number; beta?: number; dividendYield?: number }

export async function finnhubMetrics(symbol: string, apiKey: string, signal?: AbortSignal): Promise<BasicMetrics> {
  const json = (await (await get('/stock/metric', { symbol, metric: 'all' }, apiKey, signal)).json()) as { metric?: Record<string, number | null> };
  const m = json.metric ?? {};
  const num = (k: string) => (typeof m[k] === 'number' ? (m[k] as number) : undefined);
  return { pe: num('peTTM') ?? num('peBasicExclExtraTTM'), high52: num('52WeekHigh'), low52: num('52WeekLow'), beta: num('beta'), dividendYield: num('dividendYieldIndicatedAnnual') };
}

/** Verifies a key with the cheapest real call; resolves with a sample quote line for the UI. */
export async function verifyFinnhub(apiKey: string): Promise<string> {
  const q = await finnhubQuote('AAPL', apiKey);
  return `AAPL ${q.price.toFixed(2)}`;
}
